import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { z } from 'zod';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  DateField,
  Field,
  Filters,
  Modal,
  ModalActions,
  Page,
  PageHeader,
  SearchSelect,
  Select,
  Switch,
  Table,
  readAttachmentContent,
  type Action,
} from '@evogard/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@evogard/ui/primitives';
import {
  TECHNOLOGY_FEE_LOTS,
  TECHNOLOGY_FEE_LOT_LABELS,
  businessDate,
  formatDate,
  loadFailureMessage,
  type TechnologyFeeLot,
} from '@evogard/domain';
import { CONSULTANT_PROFILES } from '@evogard/contract';
import type {
  Consultant,
  ConsultantCodeCorrection,
  WireConsultantProfile,
  SalesTeam,
  SupervisorLink,
} from '@evogard/contract';

/**
 * O campo "a partir de" de toda troca de time — e por que ele existe.
 *
 * A troca muda quem responde a quem, e a premiação de líder soma os boletos
 * de quem respondia ao líder no último dia de cada competência. A data vem com
 * hoje e aceita o passado: é ela que diz que a Lara está com o Fernando desde
 * janeiro. O futuro é recusado pelo servidor.
 */
function EffectiveFromField({
  value,
  onChange,
  help,
}: {
  value: string;
  onChange: (value: string) => void;
  help: string;
}) {
  return (
    <DateField
      label="A partir de"
      required
      value={value}
      onChange={onChange}
      help={help}
    />
  );
}
import { ConsultantAwards } from './ConsultantAwards.js';
import { UserIdentityForm } from '../../components/UserIdentityForm.js';
import { api, ok } from '../../lib/api.js';
import { useDebouncedTerm } from '../../lib/debounce.js';
import { can, useSessionContext } from '../../lib/session.js';
import {
  PROFILE_LABEL,
  ProfileBadge,
  periodLabel,
  useConsultants,
  useTenantPeople,
  useTeams,
} from './common.js';

/**
 * A aba mora no endereço (28/09/2026): `/sales/consultants?tab=teams` abre
 * direto nos times, e o link de outra tela pode apontar para a lista certa.
 * Sem o parâmetro, a aba é a dos consultores, e o endereço antigo continua
 * caindo nela.
 */
export const consultantsSearch = z.object({
  tab: z.enum(['teams', 'hierarchy']).optional().catch(undefined),
});

type ConsultantsTab = 'consultants' | 'teams' | 'hierarchy';

/**
 * O cadastro do comercial: consultores, times e a cadeia de superiores.
 *
 * ## Uma tela para os três, e a razão está no contrato
 *
 * Os três vivem sob o **mesmo recurso** (`comercial.times`), e separá-los daria
 * a alguém a metade de cima da tela funcionando e a de baixo devolvendo 403 —
 * uma tela afirmando o que o código não cumpre. É a mesma forma de "fases e
 * motivos" na retenção.
 *
 * Desde 28/09/2026 os três são **abas** da mesma tela, e não três blocos na
 * mesma rolagem: a lista de consultores empurrava times e hierarquia para longe
 * da vista. Continua uma rota e uma permissão — a aba é navegação, não
 * fronteira.
 *
 * Quem concede esse recurso precisa saber o que está concedendo, e o rótulo da
 * permissão diz: este é o cadastro que decide **de quem é a comissão**. O
 * vínculo consultor ↔ código do voluntário é o que casa uma venda com uma
 * pessoa, e a hierarquia com vigência é o que faz a apuração de março continuar
 * certa depois da troca de líder em abril.
 *
 * ## O código do voluntário é POR BASE, e é a identidade do lado do ERP
 *
 * O mesmo operador é 394 no CE e 75 no GO. Por isso ele aparece em toda linha
 * que nomeia um consultor nesta casa: quem confere um número contra o ERP
 * precisa saber com qual código conferir.
 *
 * E ele **não é campo do "Salvar"**. Trocá-lo como campo reatribuiria em silêncio
 * todas as vendas já casadas por ele. Desde 16/09/2026 ele tem conserto, e o
 * conserto é um ato à parte, com ação própria (`comercial.times:corrigir_codigo`)
 * e consequência dita em voz alta: corrigir o código **solta** as vendas que o
 * código velho casou e nomeia as competências que precisam ser reapuradas.
 *
 * O caminho anterior — arquivar o vínculo errado e criar outro — deixou de ser
 * recomendado, e o motivo está no contrato: o arquivado **não sai** da apuração,
 * a premiação e a meta apontam para o id do vínculo e se perdiam na recriação, e
 * o casamento das vendas não se conserta sozinho.
 *
 * ## O casamento por nome é o defeito que este cadastro existe para tornar
 * irrepresentável
 *
 * O sistema de hoje casa consultor por identificador **ou** por nome
 * normalizado, e o "ou" é o defeito: dois homônimos veem as vendas um do outro.
 * Aqui o vínculo é explícito e por base, e não há caminho pelo qual um nome
 * parecido case com uma venda.
 *
 * ## Este cadastro decide de quem é a comissão E sob qual régua ela é paga
 *
 * Desde 28/08 as duas coisas moram aqui. Sob qual das duas réguas cada pessoa é
 * paga é dado de cadastro, e até então era dado que **nenhuma tela coletava**:
 * `comercial_consultores.perfil` existia no banco, anulável e sem padrão, e não
 * havia nem campo no contrato nem ato que o gravasse. A apuração recusava toda
 * competência com placa de alguém sem régua declarada e mandava declarar
 * **aqui** — que era justamente o que aqui não dava para fazer. Capacidade que
 * existe e não tem como ser alcançada é a família de defeito que esta casa
 * persegue, e ela não aparece em teste de rota nenhum.
 *
 * Agora dá: o ato `declarar_perfil`, no editor de cada vínculo. Ele é **ato
 * próprio**, com botão separado do "Salvar", e a separação é a decisão — ninguém
 * pode trocar a régua de alguém sem perceber que trocou quanto essa pessoa
 * recebe. Como campo do `editar`, um formulário aberto antes de outra aba
 * declarar o perfil o gravaria de volta ao valor velho, calado.
 *
 * **Desde 22/10/2026 não existe vínculo sem régua.** O nulo era o estado de
 * nascença e recusava a competência inteira — apuração, meta e ranking —, e
 * cinco cadastros incompletos deixavam o ranking da CE sem lista. O dono
 * decidiu que a régua é obrigatória: quem estava sem virou externo, e o
 * formulário de vincular pede a régua, com externo já escolhido. A fila "sem
 * régua declarada" e o filtro dela saíram junto.
 *
 * **Esta tela não explica o que são as duas réguas.** Os avisos amarelos e
 * azuis do comercial saíram em 26/10/2026, a pedido do dono; fica só o que
 * explica por que "Salvar" está travado quando a régua escolhida não foi
 * declarada.
 *
 * ## A cadeia de superiores é DERIVADA do time — desde 23/09/2026
 *
 * O time tem um líder e membros, e os membros respondem ao líder. Cada pessoa é
 * membro de um time só, e quem lidera pode continuar membro do time de cima: é
 * isso que forma a árvore (Roger → Maria → Fernando). Não há mais "abrir
 * vínculo" nesta tela — trocar alguém de time, ou trocar o líder, grava a
 * vigência com a data "a partir de" que o formulário pergunta.
 *
 * A vigência importa por dinheiro: a premiação de líder de março soma quem
 * respondia ao líder EM MARÇO. É por isso que toda troca pergunta desde quando.
 */
export function ConsultantsScreen() {
  const { data: context } = useSessionContext();
  const canEdit = can(context, 'sales.teams', 'editar');
  /*
   * A ação separada de 16/09/2026. Ela não vem junto de `editar`, então a tela
   * pergunta pelas duas: quem só a tem abre a ficha de um vínculo e enxerga um
   * bloco só — o da correção do código. Sem esta pergunta, a ação existiria no
   * catálogo e não teria tela por onde ser exercida.
   */
  const canCorrectCode = can(context, 'sales.teams', 'corrigir_codigo');
  const canSeePeople = can(context, 'platform.users', 'ver');
  /*
   * Corrigir o nome e o e-mail é da PLATAFORMA (30/10/2026): a ação é a mesma
   * de cadastrar a pessoa, e ler o e-mail de hoje pede `ver`.
   */
  const canEditPerson = can(context, 'platform.users', 'editar') && canSeePeople;
  const tab =
    (useSearch({ strict: false }) as { tab?: ConsultantsTab }).tab ?? 'consultants';
  const navigate = useNavigate();

  return (
    <Page measure="cheia">
      <PageHeader title="Consultores, times e hierarquia">
        O cadastro de{' '}
        <strong className="font-medium text-texto">{context?.currentTenant.nome}</strong>.
        O código do voluntário é desta base — a mesma pessoa tem outro código na outra
        praça.
      </PageHeader>

      <Tabs
        value={tab}
        onValueChange={(value) =>
          void navigate({
            to: '/sales/consultants',
            search: value === 'consultants' ? {} : { tab: value as ConsultantsTab },
            replace: true,
          })
        }
      >
        <TabsList>
          <TabsTrigger value="consultants">Consultores</TabsTrigger>
          <TabsTrigger value="teams">Times</TabsTrigger>
          <TabsTrigger value="hierarchy">Cadeia de superiores</TabsTrigger>
        </TabsList>

        {/*
         * `forceMount`, como na ficha da demanda: trocar de aba não pode jogar
         * fora o editor aberto. Quem está no meio de um vínculo e vai conferir
         * o time volta para o formulário como deixou.
         */}
        <TabsContent
          value="consultants"
          forceMount
          className="pt-5 data-[state=inactive]:hidden"
        >
          <ConsultantsSection
            canEdit={canEdit}
            canCorrectCode={canCorrectCode}
            canSeePeople={canSeePeople}
            canEditPerson={canEditPerson}
          />
        </TabsContent>
        <TabsContent
          value="teams"
          forceMount
          className="pt-5 data-[state=inactive]:hidden"
        >
          <TeamsSection canEdit={canEdit} canSeePeople={canSeePeople} />
        </TabsContent>
        <TabsContent
          value="hierarchy"
          forceMount
          className="pt-5 data-[state=inactive]:hidden"
        >
          <HierarchySection />
        </TabsContent>
      </Tabs>
    </Page>
  );
}

/**
 * A frase que aparece onde a lista de pessoas seria usada.
 *
 * Três formulários desta tela precisam dela, e a versão errada é sempre a mesma:
 * um seletor vazio dizendo que a base não tem ninguém, quando o que houve foi um
 * 403 na rota de usuários.
 */
function WithoutPeople() {
  return (
    <Alert tone="alerta" title="Falta a permissão que lista as pessoas da base.">
      Vincular alguém exige escolher uma pessoa, e a lista vem de “Usuários: ver”, na
      plataforma. Sem ela esta tela não pode perguntar quem existe — o que não é o mesmo
      que a base não ter ninguém. Peça a quem administra a plataforma, em Grupos.
    </Alert>
  );
}

/* ------------------------------------------------------------------------- *
 * Consultores
 * ------------------------------------------------------------------------- */

function ConsultantsSection({
  canEdit,
  canCorrectCode,
  canSeePeople,
  canEditPerson,
}: {
  canEdit: boolean;
  canCorrectCode: boolean;
  canSeePeople: boolean;
  canEditPerson: boolean;
}) {
  /*
   * A ficha abre para as DUAS ações, e não só para `editar`. Ela é o único lugar
   * de onde se corrige o código; presa atrás de `editar`, quem tivesse apenas
   * `corrigir_codigo` veria a lista com o botão escondido — capacidade sem tela,
   * que é a família de defeito que esta casa persegue. Cada bloco lá dentro
   * pergunta pela sua própria ação.
   */
  const canOpenRecord = canEdit || canCorrectCode || canEditPerson;
  const [showInactive, setShowInactive] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  const consultantsQuery = useConsultants({ includeInactive: showInactive });
  const consultant = consultantsQuery.data?.find((c) => c.id === editing);

  /*
   * A busca recorta no cliente: a rota já devolve a base inteira, e são
   * dezenas de vínculos, não milhares. Procura no que a linha mostra — nome,
   * código do voluntário e time.
   */
  const term = folded(search.trim());
  const listed = (consultantsQuery.data ?? []).filter(
    (c) =>
      term === '' ||
      folded(`${c.user.nome} ${c.erpVolunteerCode} ${c.team?.nome ?? ''}`).includes(term),
  );

  return (
    <div className="flex flex-col gap-3">
      {consultantsQuery.isError && (
        <Alert tone="erro" title="Não foi possível carregar os consultores.">
          {consultantsQuery.error.message}
        </Alert>
      )}

      {creating && canEdit && (
        <ConsultantForm canSeePeople={canSeePeople} toClose={() => setCreating(false)} />
      )}

      {canOpenRecord && consultant && (
        <ConsultantEditor
          key={consultant.id}
          consultant={consultant}
          canEdit={canEdit}
          canCorrectCode={canCorrectCode}
          canEditPerson={canEditPerson}
          toClose={() => setEditing(null)}
        />
      )}

      {(!consultantsQuery.isError || consultantsQuery.data) && (
        <Card
          title="Consultores"
          descricao="Quem vende nesta base, e com que código do ERP as vendas dele casam."
          bleed
        >
          <Table
            /*
             * Os dois recortes desta lista no painel, e o botão de criar na
             * barra — e não mais uma caixa de marcar e um botão empilhados no
             * cabeçalho do cartão.
             *
             * As duas caixas eram `<input type="checkbox">` escritos à mão, uma
             * no cabeçalho e outra DENTRO do aviso da fila: dois lugares
             * diferentes recortando a mesma lista, nenhum deles com a forma que
             * o resto da casa usa. Viraram `selecao` de duas opções pelo motivo
             * escrito em `FilterField` — dentro de um painel, a caixa não diz
             * qual é o outro estado dela.
             */
            toolbar={
              <>
                <Filters
                  values={{
                    inactive: showInactive ? 'sim' : '',
                  }}
                  onApply={(values) => {
                    setShowInactive(values.inactive === 'sim');
                  }}
                  fields={[
                    {
                      type: 'selecao',
                      name: 'inactive',
                      label: 'Quem parou de vender',
                      placeholder: 'Só quem vende hoje',
                      options: [{ value: 'sim', label: 'Ativos e inativos' }],
                    },
                  ]}
                />
                {canEdit && (
                  <Button onClick={() => setCreating(true)}>Vincular pessoa</Button>
                )}
              </>
            }
            search={{
              label: 'Pesquisar consultores',
              placeholder: 'Nome, código ou time',
              value: search,
              onChange: setSearch,
            }}
            label="Consultores desta base"
            items={listed}
            loading={consultantsQuery.isPending}
            rowKey={(c) => String(c.id)}
            empty={
              term !== ''
                ? `Nenhum consultor com “${search.trim()}” no nome, no código ou no time.`
                : showInactive
                  ? 'Nenhum consultor nesta base, nem inativo. Sem consultor vinculado, nenhuma venda importada encontra dono — e comissão sem dono não é de ninguém.'
                  : 'Nenhum consultor ativo nesta base. Se alguém já vendeu aqui, escolha “Ativos e inativos” em Filtros: reativar é diferente de vincular pela primeira vez.'
            }
            rowTitle={(c) => c.user.nome}
            rowSubtitle={(c) => `Código ${c.erpVolunteerCode}`}
            rowIcon="cracha"
            /*
             * A ação da linha no painel das reticências, e não mais um botão
             * "Editar" numa coluna chamada "Ação".
             *
             * **O vínculo arquivado recebe o motivo escrito**, em vez de perder
             * o gatilho em silêncio: antes disto a célula ficava vazia, e nada
             * dizia por que uma linha tinha botão e a vizinha não. Arquivado é
             * "este vínculo nunca deveria ter existido" — editá-lo seria
             * consertar uma linha que a casa já disse ser um engano.
             */
            rowActions={(c): Action[] =>
              canOpenRecord
                ? [
                    {
                      label: 'Editar',
                      icon: 'etiqueta',
                      disabled: c.archivedAt
                        ? 'Este vínculo está arquivado — ele nunca deveria ter existido.'
                        : (false as const),
                      run: () => setEditing(c.id),
                    },
                  ]
                : []
            }
            /*
             * O clique na linha também abre a ficha (30/10/2026) — a ação
             * "Editar" continua só nas reticências. O arquivado não abre, pelo
             * motivo escrito no painel.
             */
            onRowClick={
              canOpenRecord
                ? (c) => {
                    if (!c.archivedAt) setEditing(c.id);
                  }
                : undefined
            }
            columns={[
              {
                title: 'Pessoa',
                alignment: 'inicio' as const,
                minWidth: '14rem',
                cell: (c) => (
                  <div className="flex min-w-0 items-center gap-3">
                    <Avatar nome={c.user.nome} photoUrl={c.photoUrl} />
                    <div className="min-w-0">
                      <p
                        className={
                          c.archivedAt ? 'truncate text-texto-suave' : 'truncate'
                        }
                      >
                        {c.user.nome}
                      </p>
                      <p className="mt-0.5 truncate text-xs tabular-nums text-texto-suave">
                        código {c.erpVolunteerCode}
                      </p>
                    </div>
                  </div>
                ),
              },
              {
                title: 'Time',
                alignment: 'inicio' as const,
                cell: (c) =>
                  c.team ? (
                    c.team.nome
                  ) : (
                    // "sem time" é estado legítimo, e não pendência
                    <span className="text-texto-suave">Sem time</span>
                  ),
              },
              {
                /*
                 * A régua ao lado da pessoa, e não escondida no editor: quem é
                 * da base de reativação se distingue na lista sem abrir ninguém.
                 */
                title: 'Régua',
                alignment: 'inicio' as const,
                cell: (c) => <ProfileBadge profile={c.profile} />,
              },
              {
                title: 'Situação',
                cell: (c) => <ConsultantStatus consultant={c} />,
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}

/**
 * As três situações de um vínculo, e por que elas não são a mesma coisa.
 *
 * `ativo = false` é **a pessoa parou de vender**: sai do ranking corrente e
 * continua dona das vendas antigas e da comissão já apurada. `arquivado` é
 * **este vínculo nunca deveria ter existido** — código digitado errado, usuário
 * trocado —, e sai de tudo, inclusive do passado.
 *
 * Confundir os dois apaga a comissão apurada de quem apenas parou de vender, e é
 * a razão de os dois serem atos separados no contrato e selos diferentes aqui.
 *
 * Vender é o esperado, e o esperado não leva selo: quem está ativo aparece sem
 * marca nenhuma. Um selo verde em toda linha gastaria a atenção que os outros
 * dois precisam comprar.
 */
/** Sem acento e em minúscula — "joao" acha "João". */
function folded(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

function ConsultantStatus({ consultant }: { consultant: Consultant }) {
  if (consultant.archivedAt) {
    return <Badge tone="neutro">Arquivado — o vínculo não deveria existir</Badge>;
  }
  if (!consultant.active) {
    return <Badge tone="alerta">Parou de vender</Badge>;
  }
  return <span className="text-texto-suave">—</span>;
}

/** Vincular uma pessoa ao código de voluntário desta base. */
function ConsultantForm({
  canSeePeople,
  toClose,
}: {
  canSeePeople: boolean;
  toClose: () => void;
}) {
  const client = useQueryClient();
  const [userId, setUserId] = useState('');
  const [erpVolunteerCode, setCodeVolunteer] = useState('');
  /* o rótulo da escolha: a lista da busca muda a cada termo, e a opção some dela */
  const [volunteerLabel, setVolunteerLabel] = useState('');
  const [teamId, setTeamId] = useState('');
  /* A régua é obrigatória desde 22/10/2026, e nasce em externo — o fluxo de hoje. */
  const [profile, setProfile] = useState<WireConsultantProfile>('externo');
  const [effectiveFrom, setEffectiveFrom] = useState(businessDate(new Date()));
  const [photo, setPhoto] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const people = useTenantPeople({ enabled: canSeePeople });
  const teams = useTeams();
  /*
   * Quem já tem vínculo nesta base não aparece na Pessoa (03/11/2026), como o
   * código já vinculado não aparece no campo do código. O banco recusa a
   * segunda linha (`sales_consultants_user_uidx`), mas a lista oferecia a
   * pessoa e a recusa só vinha no "Vincular". Quem parou de vender continua
   * vinculado e fica de fora; o vínculo ARQUIVADO não conta, e a pessoa volta.
   */
  const linked = useConsultants({ enabled: canSeePeople, includeInactive: true });
  const linkedUsers = new Set((linked.data ?? []).map((c) => c.user.id));
  const available = (people.data?.items ?? []).filter((p) => !linkedUsers.has(p.id));

  const create = useMutation({
    mutationFn: async () => {
      const r = await api.sales.createConsultant({
        body: {
          userId,
          erpVolunteerCode: erpVolunteerCode.trim(),
          teamId: teamId ? Number(teamId) : null,
          profile,
          effectiveFrom,
          ...(photo === null ? {} : { photo: await photoUpload(photo) }),
        },
      });
      return ok(r, 201, 'Não foi possível vincular a pessoa.');
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['comercial'] });
      toClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Modal
      open
      onOpenChange={(next) => {
        if (!next) toClose();
      }}
      size="xl"
      title="Vincular pessoa"
      descricao="O código do voluntário casa as vendas importadas com a pessoa."
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          create.mutate();
        }}
      >
        {error && <Alert tone="erro">{error}</Alert>}
        {!canSeePeople && <WithoutPeople />}

        <div className="grid gap-8 lg:grid-cols-[17rem_minmax(0,1fr)]">
          <aside className="lg:border-r lg:border-borda lg:pr-8">
            <PhotoField
              nome={people.data?.items.find((p) => p.id === userId)?.nome ?? null}
              currentUrl={null}
              file={photo}
              onChange={setPhoto}
            >
              {photo !== null && (
                <Button
                  type="button"
                  variant="discreto"
                  size="sm"
                  onClick={() => setPhoto(null)}
                >
                  Desfazer
                </Button>
              )}
            </PhotoField>
          </aside>

          <div className="grid content-start gap-4 sm:grid-cols-2">
            <SearchSelect
              label="Pessoa"
              required
              disabled={!canSeePeople}
              placeholder="Escolha…"
              searchPlaceholder="Nome ou e-mail"
              emptyMessage="Ninguém sem vínculo nesta base com esse nome ou e-mail."
              loading={(people.isPending || linked.isPending) && canSeePeople}
              error={linked.isError ? linked.error.message : null}
              options={available.map((p) => ({
                value: p.id,
                label: `${p.nome} · ${p.email}`,
              }))}
              value={userId}
              onChange={setUserId}
              help="Só aparece quem ainda não tem vínculo nesta base."
            />

            <VolunteerCodeField
              value={erpVolunteerCode}
              selectedLabel={volunteerLabel}
              onChange={(code, label) => {
                setCodeVolunteer(code);
                setVolunteerLabel(label);
              }}
            />

            <Select
              label="Régua"
              required
              value={profile}
              onChange={(e) => setProfile(e.target.value as WireConsultantProfile)}
              help="Sob qual régua a pessoa é paga."
            >
              {CONSULTANT_PROFILES.map((value) => (
                <option key={value} value={value}>
                  {PROFILE_LABEL[value]}
                </option>
              ))}
            </Select>

            <SearchSelect
              label="Time"
              placeholder="Sem time"
              searchPlaceholder="Nome do time"
              emptyMessage="Nenhum time com esse nome."
              clearable
              loading={teams.isPending}
              options={(teams.data ?? []).map((t) => ({
                value: String(t.id),
                label: t.nome,
              }))}
              value={teamId}
              onChange={setTeamId}
            />

            {teamId && (
              <EffectiveFromField
                value={effectiveFrom}
                onChange={setEffectiveFrom}
                help="Desde quando responde ao líder do time."
              />
            )}
          </div>
        </div>

        <ModalActions>
          <Button
            type="submit"
            loading={create.isPending}
            disabled={!userId || !erpVolunteerCode.trim()}
          >
            Vincular
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
}

/**
 * O código do voluntário, escolhido PESQUISANDO o cadastro do ERP (30/10/2026).
 *
 * Digitar o código de cabeça era onde o vínculo nascia errado: um dígito
 * trocado casa as vendas de outra pessoa, e nada reclama. Aqui se pesquisa pelo
 * nome (ou pelo código), a lista mostra o código ao lado, e o escolhido é o que
 * vai. Só aparece quem ainda não tem vínculo — o mesmo recorte de ERP ›
 * Voluntários.
 *
 * Pesquisar o cadastro é `erp.catalog:ver`. Sem ela, o campo volta a ser
 * digitado, como antes — vincular não pode depender de uma permissão de outro
 * módulo.
 */
function VolunteerCodeField({
  value,
  selectedLabel,
  onChange,
}: {
  value: string;
  selectedLabel: string;
  onChange: (code: string, label: string) => void;
}) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const canSearch = can(context, 'erp.catalog', 'ver');
  const [term, setTerm] = useState('');
  const searched = useDebouncedTerm(term.trim());

  const volunteers = useQuery({
    queryKey: ['erp', 'voluntarios', tenantId, 'sem-vinculo', searched],
    enabled: canSearch,
    queryFn: async () => {
      const r = await api.erp.searchVolunteers({
        body: {
          /* menos de duas letras não vai: o `min(2)` do contrato recusaria */
          term: searched.length >= 2 ? searched : undefined,
          link: 'sem_vinculo',
          page: 1,
          perPage: 20,
        },
      });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'os voluntários'));
      return r.body;
    },
    placeholderData: (previous) => previous,
  });

  if (!canSearch) {
    return (
      <Field
        label="Código do voluntário nesta base"
        required
        maxLength={40}
        value={value}
        onChange={(e) => onChange(e.target.value, '')}
        help="O código do ERP desta praça."
      />
    );
  }

  /* sem código no ERP não há o que gravar: o código é o que liga as vendas */
  const options = (volunteers.data?.items ?? []).flatMap((v) =>
    v.erpCode === null
      ? []
      : [{ value: v.erpCode, label: v.nome ?? 'Sem nome no ERP', aside: v.erpCode }],
  );

  return (
    <SearchSelect
      label="Código do voluntário nesta base"
      required
      placeholder="Pesquise pelo nome"
      searchPlaceholder="Nome ou código"
      emptyMessage="Nenhum voluntário sem vínculo com esse nome ou código."
      loading={volunteers.isFetching}
      error={volunteers.isError ? volunteers.error.message : null}
      options={options}
      value={value}
      selectedLabel={selectedLabel || undefined}
      onSearchChange={setTerm}
      onChange={(code) => {
        const chosen = options.find((o) => o.value === code);
        onChange(code, chosen ? `${chosen.label} · ${chosen.value}` : '');
      }}
      help="Só aparece quem ainda não tem vínculo nesta base."
    />
  );
}

/**
 * Editar um vínculo — trocar de time, tirar de circulação, declarar a régua de
 * pagamento, corrigir o código do ERP, ou arquivar.
 *
 * Os cinco atos têm botões separados e textos diferentes porque respondem
 * perguntas diferentes. O arquivar não é destrutivo por acidente: ele é a
 * declaração de que o vínculo nunca deveria ter existido, e sai da apuração
 * inteira — inclusive do passado.
 *
 * ## Um dos cinco exige OUTRA permissão, e por isso a ficha tem duas chaves
 *
 * Corrigir o código pede `comercial.times:corrigir_codigo`; os outros quatro
 * pedem `:editar`. Quem tem uma e não a outra abre esta ficha e vê só os blocos
 * que pode usar — nenhum botão aparece desabilitado sem explicação, e nenhum
 * bloco aparece prometendo um 403. É o mesmo desenho das premiações logo abaixo,
 * que pedem `comercial.comissoes:configurar_premiacao`.
 *
 * ## `declarar_perfil` é ato PRÓPRIO, e a separação é a decisão
 *
 * **Ninguém pode trocar a régua de alguém sem perceber que trocou quanto essa
 * pessoa recebe.** Como campo do "Salvar", toda edição de time mandaria o perfil
 * junto — e um formulário carregado antes de outra aba declarar a régua a
 * gravaria de volta ao valor velho, calado. O ato separado não tem isso: quem o
 * envia está dizendo só isso, e a trilha registra `de → para`.
 *
 * Ele **não anula**. Apagar a régua de alguém pararia a apuração da competência
 * inteira da praça, de todo mundo, e ninguém pede isso ao corrigir um cadastro —
 * régua declarada errada se corrige declarando a certa.
 *
 * ## Ele NÃO fecha o cartão ao dar certo, e os outros fecham
 *
 * O único ato desta tela cujo efeito não aparece no lugar de onde se clicou: o
 * time e o "está vendendo" mudam a linha da lista, e a régua muda quanto a
 * pessoa recebe. Fechar levaria a confirmação embora junto com o cartão, e o
 * selo novo na lista é fácil demais de não notar num ato deste tamanho.
 */
function ConsultantEditor({
  consultant,
  canEdit,
  canCorrectCode,
  canEditPerson,
  toClose,
}: {
  consultant: Consultant;
  canEdit: boolean;
  canCorrectCode: boolean;
  canEditPerson: boolean;
  toClose: () => void;
}) {
  const client = useQueryClient();
  const [teamId, setTeamId] = useState(consultant.team ? String(consultant.team.id) : '');
  const [active, setActive] = useState(consultant.active);
  /*
   * O "A partir de" mostra a data GRAVADA (30/10/2026): enquanto o time é o
   * mesmo e a cadeia aponta para o líder dele, o campo abre com o começo do
   * vínculo aberto, e não com hoje — reabrir a ficha e ver hoje parecia que o
   * salvar não tinha gravado. Trocar o time volta para hoje, que é quando a
   * troca começaria; a pessoa ajusta se for outra data.
   */
  const [dateTyped, setDateTyped] = useState<string | null>(null);
  const [profile, setProfile] = useState<WireConsultantProfile | ''>(consultant.profile);
  const [error, setError] = useState<string | null>(null);
  const [ladderDeclared, setLadderDeclared] = useState<WireConsultantProfile | null>(
    null,
  );
  const teams = useTeams();
  const chain = useConsultantChain(consultant.user.id);

  const selectedTeam = (teams.data ?? []).find((t) => String(t.id) === teamId);
  const selectedLeader = selectedTeam?.leader ?? null;
  /* sem time, time sem líder, ou o próprio time: não há a quem responder, nem data */
  const answersToSomeone =
    selectedLeader !== null && selectedLeader.id !== consultant.user.id;
  const openLink = chain.data?.find(
    (l) => l.validUntil === null && l.archivedAt === null,
  );
  const storedDate =
    teamId === (consultant.team ? String(consultant.team.id) : '') &&
    openLink !== undefined &&
    openLink.supervisor.id === selectedLeader?.id
      ? openLink.validFrom
      : null;
  const effectiveFrom = dateTyped ?? storedDate ?? businessDate(new Date());

  /*
   * "Membro atualmente no time" (03/11/2026), marcado por padrão. Desmarcado,
   * a ficha pede o "Foi membro até" — o último dia no time —, e o salvar
   * grava o período inteiro: no time de "A partir de" até o fim, e sem time
   * depois. A API faz os dois passos numa transação (`effectiveUntil`).
   */
  const [currentMember, setCurrentMember] = useState(true);
  const [until, setUntil] = useState('');

  /*
   * A régua escolhida no seletor e ainda NÃO declarada (28/09/2026). "Salvar"
   * grava só time, "está vendendo" e vigência, e fecha o editor — com a régua
   * trocada e não declarada, ele fechava calado e a escolha sumia: a pessoa
   * via 200, o vínculo continuava externo, e nada dizia por quê. Enquanto há
   * régua pendente, "Salvar" fica travado e diz qual botão falta.
   */
  const currentProfile = ladderDeclared ?? consultant.profile;
  const profilePending = profile !== '' && profile !== currentProfile;

  const save = useMutation({
    mutationFn: async (
      body:
        | {
            action: 'editar';
            teamId: number | null;
            active: boolean;
            effectiveFrom: string;
            effectiveUntil?: string;
          }
        | { action: 'declarar_perfil'; profile: WireConsultantProfile }
        | { action: 'arquivar' },
    ) => {
      const r = await api.sales.editConsultant({
        params: { id: consultant.id },
        body: body,
      });
      return ok(r, 200, 'Não foi possível salvar o vínculo.');
    },
    onSuccess: (updated, variables) => {
      void client.invalidateQueries({ queryKey: ['comercial'] });
      if (variables.action === 'declarar_perfil') {
        setError(null);
        /* O que o SERVIDOR gravou, e não o que o seletor tinha: se os dois
           divergirem, quem manda é a linha. */
        setLadderDeclared(updated.profile);
        return;
      }
      toClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Modal
      open
      onOpenChange={(next) => {
        if (!next) toClose();
      }}
      size="xl"
      title={consultant.user.nome}
      descricao={`Código ${consultant.erpVolunteerCode} nesta base`}
      titleAddon={<ProfileBadge profile={currentProfile} />}
    >
      {/*
       * Duas colunas desde 30/10/2026: à esquerda a PESSOA (foto, nome e
       * e-mail, que valem em todas as bases), à direita o VÍNCULO desta base.
       * No telefone as duas empilham, a pessoa primeiro.
       */}
      <div className="grid gap-8 lg:grid-cols-[17rem_minmax(0,1fr)]">
        <aside className="flex flex-col gap-6 lg:border-r lg:border-borda lg:pr-8">
          {canEdit ? (
            <PhotoBlock consultant={consultant} />
          ) : (
            <div className="flex justify-center">
              <Avatar
                nome={consultant.user.nome}
                photoUrl={consultant.photoUrl}
                size="retrato"
              />
            </div>
          )}

          {canEditPerson && (
            <EditorSection title="Usuário">
              <UserIdentityForm userId={consultant.user.id} />
            </EditorSection>
          )}
        </aside>

        <div className="flex min-w-0 flex-col gap-8">
          {error && <Alert tone="erro">{error}</Alert>}

          {canEdit && (
            <EditorSection title="Vínculo">
              <div className="grid gap-4 sm:grid-cols-2">
                <Select
                  label="Time"
                  value={teamId}
                  onChange={(e) => {
                    setTeamId(e.target.value);
                    setDateTyped(null);
                  }}
                >
                  <option value="">Sem time</option>
                  {(teams.data ?? []).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nome}
                    </option>
                  ))}
                </Select>

                {/*
                 * Visível com o MESMO time também: salvar grava a vigência de
                 * quem ainda não respondia ao líder — o cadastro de antes de
                 * 23/09 se conserta por aqui. Some só quando não há líder a quem
                 * responder, e aí a data não mudaria nada.
                 */}
                {answersToSomeone && (
                  <EffectiveFromField
                    value={effectiveFrom}
                    onChange={setDateTyped}
                    help={`Desde quando responde a ${selectedLeader.nome}.`}
                  />
                )}
                {answersToSomeone && (
                  <label className="flex min-h-11 items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={currentMember}
                      onChange={(e) => setCurrentMember(e.target.checked)}
                      className="size-4 rounded border-borda-forte accent-primaria-texto"
                    />
                    Membro atualmente no time
                  </label>
                )}
                {answersToSomeone && !currentMember && (
                  <DateField
                    label="Foi membro até"
                    required
                    value={until}
                    onChange={setUntil}
                    help={`Último dia no time — e respondendo a ${selectedLeader.nome}.`}
                  />
                )}
              </div>

              <CurrentSupervisor consultant={consultant} teams={teams.data} />

              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium">Situação</span>
                  <Switch
                    label="Está vendendo"
                    checked={active}
                    onCheckedChange={setActive}
                    onLabel="Vendendo"
                    offLabel="Parou de vender"
                  />
                </div>

                <Button
                  loading={save.isPending && save.variables?.action === 'editar'}
                  disabled={profilePending}
                  onClick={() => {
                    setError(null);
                    const leaving = answersToSomeone && !currentMember;
                    if (leaving && until === '') {
                      setError('Informe até quando a pessoa foi membro do time.');
                      return;
                    }
                    save.mutate({
                      action: 'editar',
                      teamId: teamId ? Number(teamId) : null,
                      active,
                      effectiveFrom,
                      ...(leaving ? { effectiveUntil: until } : {}),
                    });
                  }}
                >
                  Salvar vínculo
                </Button>
              </div>

              {profilePending && (
                <Alert tone="alerta">
                  A régua mudou e não foi declarada: use “Declarar” em Remuneração, ou
                  volte para {PROFILE_LABEL[currentProfile]}.
                </Alert>
              )}
            </EditorSection>
          )}

          {canEdit && (
            <EditorSection title="Remuneração">
              <div className="grid gap-4 sm:grid-cols-2">
                <EditorTile>
                  <Select
                    label="Régua"
                    value={profile}
                    onChange={(e) =>
                      setProfile(e.target.value as WireConsultantProfile | '')
                    }
                    help="O interno (CLT) não envia nota e só tem o lote 1x."
                  >
                    {CONSULTANT_PROFILES.map((value) => (
                      <option key={value} value={value}>
                        {PROFILE_LABEL[value]}
                      </option>
                    ))}
                  </Select>
                  {ladderDeclared !== null && (
                    <Alert tone="sucesso">
                      Declarada. O que já foi apurado só muda apurando de novo.
                    </Alert>
                  )}
                  <div className="flex justify-end">
                    <Button
                      variant="contorno"
                      loading={
                        save.isPending && save.variables?.action === 'declarar_perfil'
                      }
                      disabled={profile === '' || !profilePending}
                      onClick={() => {
                        if (profile === '') return;
                        setError(null);
                        setLadderDeclared(null);
                        save.mutate({ action: 'declarar_perfil', profile });
                      }}
                    >
                      Declarar
                    </Button>
                  </div>
                </EditorTile>

                <TechnologyFeeLotBlock consultant={consultant} />
              </div>
            </EditorSection>
          )}

          {/*
           * As premiações vêm depois da remuneração e antes das correções: a
           * ordem é a das consequências. Ela traz o próprio `Card` — é um
           * cadastro dentro do cadastro, e sem a borda "Arquivar" logo abaixo
           * pareceria arquivar a premiação.
           */}
          <ConsultantAwards consultant={consultant} />

          {(canCorrectCode || canEdit) && (
            <EditorSection title="Correções">
              <div className="grid gap-4 sm:grid-cols-2">
                {canCorrectCode && <CodeCorrection consultant={consultant} />}

                {canEdit && (
                  <EditorTile
                    title="Arquivar o vínculo"
                    descricao="Para vínculo que nunca deveria ter existido. Sai de tudo, inclusive do passado."
                  >
                    <div className="mt-auto flex justify-end">
                      <Button
                        variant="perigo"
                        loading={save.isPending && save.variables?.action === 'arquivar'}
                        onClick={() => {
                          setError(null);
                          save.mutate({ action: 'arquivar' });
                        }}
                      >
                        Arquivar
                      </Button>
                    </div>
                  </EditorTile>
                )}
              </div>
            </EditorSection>
          )}
        </div>
      </div>
    </Modal>
  );
}

/** O título de cada bloco da ficha — pequeno, em caixa alta, e só ele. */
/**
 * A quem a pessoa responde HOJE, e desde quando (30/10/2026).
 *
 * O "A partir de" não é guardado no vínculo: é a data em que a troca de time
 * passa a valer na cadeia, e a ficha sempre reabre com hoje. Sem esta linha,
 * salvar parecia não ter feito nada — principalmente para o líder do próprio
 * time, que não responde a ninguém e em quem a data não muda coisa alguma.
 * Lê a cadeia gravada (`listHierarchy`), e não deduz do time: o time diz a
 * quem a pessoa DEVERIA responder; a cadeia diz a quem responde.
 */
function CurrentSupervisor({
  consultant,
  teams,
}: {
  consultant: Consultant;
  teams: SalesTeam[] | undefined;
}) {
  const userId = consultant.user.id;
  const chain = useConsultantChain(userId);

  if (chain.isPending) return null;
  if (chain.isError) {
    return <p className="text-xs text-texto-suave">{chain.error.message}</p>;
  }

  const open = chain.data.find((l) => l.validUntil === null && l.archivedAt === null);
  const team = teams?.find((t) => t.id === consultant.team?.id);
  const ledHere = team?.leader?.id === userId;
  const expected = ledHere ? null : (team?.leader ?? null);

  let text: ReactNode;
  if (open) {
    text = (
      <>
        Responde a <strong className="text-texto">{open.supervisor.nome}</strong> desde{' '}
        {formatDate(open.validFrom)}.
      </>
    );
  } else if (consultant.team === null) {
    text = 'Sem superior: não está em nenhum time.';
  } else if (ledHere) {
    text = 'Sem superior: lidera o próprio time.';
  } else if (expected === null) {
    text = 'Sem superior: o time não tem líder.';
  } else {
    text = (
      <>
        Ainda não responde a <strong className="text-texto">{expected.nome}</strong>.
        Salve com a data em que entrou no time.
      </>
    );
  }

  /* o time diz um líder e a cadeia aberta diz outro: salvar realinha */
  const diverges =
    open !== undefined && expected !== null && open.supervisor.id !== expected.id;

  return (
    <p className="text-xs text-texto-suave">
      {text}
      {diverges && (
        <>
          {' '}
          O time é de <strong className="text-texto">{expected.nome}</strong>; salve para
          trocar.
        </>
      )}
    </p>
  );
}

/** A cadeia gravada de uma pessoa — a ficha e a linha de baixo leem a mesma. */
function useConsultantChain(userId: string) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  return useQuery({
    queryKey: ['comercial', 'hierarquia', tenantId, 'pessoa', userId],
    queryFn: async () => {
      const r = await api.sales.listHierarchy({ query: { userId } });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'a cadeia desta pessoa'));
      }
      return r.body;
    },
  });
}

function EditorSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-xs font-semibold tracking-wide text-texto-suave uppercase">
        {title}
      </h3>
      {children}
    </section>
  );
}

/**
 * Um ato da ficha que tem botão próprio — a régua, o lote da taxa, o código e
 * o arquivar. A borda é o que diz que o botão de dentro salva só aquilo.
 */
function EditorTile({
  title,
  descricao,
  children,
}: {
  title?: string;
  descricao?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-cartao border border-borda p-4">
      {title && (
        <div>
          <p className="text-sm font-semibold">{title}</p>
          {descricao && <p className="mt-1 text-sm text-texto-suave">{descricao}</p>}
        </div>
      )}
      {children}
    </div>
  );
}

/** Os formatos que o servidor aceita — o seletor de arquivo já filtra por eles. */
const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp';

/** O arquivo escolhido, na forma do corpo da requisição. */
async function photoUpload(file: File) {
  return {
    nome: file.name,
    typeMime: file.type || 'application/octet-stream',
    content: await readAttachmentContent(file),
  };
}

/**
 * O retrato com o botão de escolher a foto embaixo.
 *
 * O `<input type="file">` fica escondido e quem o abre é um `Button` — o campo
 * nativo desenha "Nenhum arquivo escolhido" na língua do navegador e não
 * aceita a aparência da casa. A prévia é do arquivo ESCOLHIDO, antes de
 * enviar, ou da foto atual; sem nenhuma, as iniciais. Quem diz se o arquivo
 * serve é o servidor (JPEG, PNG ou WebP, até 5 MB).
 */
function PhotoField({
  nome,
  currentUrl,
  file,
  onChange,
  children,
}: {
  nome: string | null;
  currentUrl: string | null;
  file: File | null;
  onChange: (file: File | null) => void;
  /** Os botões que acompanham o de escolher — salvar, remover, desfazer. */
  children?: ReactNode;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(() => {
    if (file === null) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const hasPhoto = file !== null || currentUrl !== null;

  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <Avatar nome={nome ?? '?'} photoUrl={preview ?? currentUrl} size="retrato" />
      <input
        ref={input}
        type="file"
        accept={PHOTO_ACCEPT}
        className="hidden"
        onChange={(e) => {
          onChange(e.target.files?.[0] ?? null);
          /* limpo, para escolher o MESMO arquivo de novo disparar o `change` */
          e.target.value = '';
        }}
      />
      <div className="flex flex-wrap justify-center gap-2">
        <Button
          type="button"
          variant="contorno"
          size="sm"
          icon="camera"
          onClick={() => input.current?.click()}
        >
          {hasPhoto ? 'Trocar foto' : 'Escolher foto'}
        </Button>
        {children}
      </div>
      <p className="max-w-full truncate text-xs text-texto-suave">
        {file ? file.name : 'JPEG, PNG ou WebP · até 5 MB'}
      </p>
    </div>
  );
}

/**
 * A FOTO do vínculo, na ficha (29/10/2026, pedido do dono).
 *
 * Ato próprio, como a régua e o lote da taxa: "Salvar vínculo" não envia a
 * foto. Escolher mostra a prévia e pede "Salvar"; só então ela vai.
 */
function PhotoBlock({ consultant }: { consultant: Consultant }) {
  const client = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async (next: File | null) => {
      const r = await api.sales.editConsultant({
        params: { id: consultant.id },
        body: {
          action: 'definir_foto',
          photo: next === null ? null : await photoUpload(next),
        },
      });
      return ok(r, 200, 'Não foi possível salvar a foto.');
    },
    onSuccess: () => {
      setError(null);
      setFile(null);
      void client.invalidateQueries({ queryKey: ['comercial'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="flex flex-col gap-3">
      <PhotoField
        nome={consultant.user.nome}
        currentUrl={consultant.photoUrl}
        file={file}
        onChange={(next) => {
          setError(null);
          setFile(next);
        }}
      >
        {file !== null ? (
          <>
            <Button
              type="button"
              size="sm"
              loading={save.isPending}
              onClick={() => save.mutate(file)}
            >
              Salvar
            </Button>
            <Button
              type="button"
              variant="discreto"
              size="sm"
              disabled={save.isPending}
              onClick={() => setFile(null)}
            >
              Desfazer
            </Button>
          </>
        ) : (
          consultant.photoUrl !== null && (
            <Button
              type="button"
              variant="discreto"
              size="sm"
              loading={save.isPending}
              onClick={() => save.mutate(null)}
            >
              Remover
            </Button>
          )
        )}
      </PhotoField>
      {error && <Alert tone="erro">{error}</Alert>}
    </div>
  );
}

/**
 * CORRIGIR O CÓDIGO DO ERP — o ato que o cadastro não tinha até 16/09/2026.
 *
 * ## Por que ele não é um campo do “Salvar”
 *
 * O código é a identidade do consultor do lado do ERP, e o casamento da venda com
 * a pessoa é **congelado na importação**: trocá-lo como campo deixaria as vendas
 * que o código velho casou penduradas aqui, agora afirmando ser do código novo.
 * Por isso o ato é sozinho, tem ação própria e **solta** essas vendas.
 *
 * ## O que a tela mostra DEPOIS, e por que ela não promete antes
 *
 * Quantas vendas foram soltas e quais competências precisam ser reapuradas são
 * números do servidor, e só existem depois do ato. Esta tela não os estima antes
 * do clique: um “isto vai soltar ~40 vendas” contado no front seria a tela
 * afirmando o que o código não conferiu. O que ela diz antes é o que é certo — que
 * solta, que a importação recasa, e que o que já foi apurado não se refaz sozinho.
 */
function CodeCorrection({ consultant }: { consultant: Consultant }) {
  const client = useQueryClient();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<ConsultantCodeCorrection | null>(null);

  const typed = code.trim();
  /* A mesma recusa que o servidor faz, e não uma segunda regra: corrigir para o
   * mesmo código não muda cadastro nenhum e soltaria a carteira à toa. Quem
   * protege é o serviço; aqui o botão só não convida para o erro. */
  const ready = typed.length > 0 && typed !== consultant.erpVolunteerCode;

  const correct = useMutation({
    mutationFn: async () => {
      const r = await api.sales.correctConsultantCode({
        params: { id: consultant.id },
        body: { erpVolunteerCode: typed },
      });
      return ok(r, 200, 'Não foi possível corrigir o código.');
    },
    onSuccess: (result) => {
      void client.invalidateQueries({ queryKey: ['comercial'] });
      setError(null);
      /* O que o SERVIDOR fez, e não o que o campo tinha — é dele que saem os
         dois números que esta tela precisa mostrar. */
      setDone(result);
      setCode('');
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <EditorTile
      title="Corrigir o código do ERP"
      descricao="Para código digitado errado. Solta as vendas casadas pelo código atual."
    >
      {error && <Alert tone="erro">{error}</Alert>}

      <Field
        label="Código certo"
        maxLength={40}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder={consultant.erpVolunteerCode}
      />

      {done !== null && (
        <Alert
          tone="sucesso"
          title={`Era ${done.previousCode}, agora é ${done.consultant.erpVolunteerCode}.`}
        >
          <p className="mt-1">
            {done.salesDetached === 0
              ? 'Nenhuma venda estava casada por ele.'
              : `${done.salesDetached} ${done.salesDetached === 1 ? 'venda volta' : 'vendas voltam'} a casar na próxima importação.`}{' '}
            {done.periodsToReassess.length > 0 &&
              `Reapurar em Comissões: ${done.periodsToReassess.map(periodLabel).join(', ')}.`}
          </p>
        </Alert>
      )}

      {/*
       * `perigo` como o arquivar: não há diálogo de confirmação nesta casa
       * ainda, e o que separa o ponteiro de uma carteira inteira ficar sem dono
       * é a cor do botão.
       */}
      <div className="mt-auto flex justify-end">
        <Button
          variant="perigo"
          loading={correct.isPending}
          disabled={!ready}
          onClick={() => {
            if (!ready) return;
            setError(null);
            setDone(null);
            correct.mutate();
          }}
        >
          Corrigir
        </Button>
      </div>
    </EditorTile>
  );
}

/* ------------------------------------------------------------------------- *
 * Times
 * ------------------------------------------------------------------------- */

function TeamsSection({
  canEdit,
  canSeePeople,
}: {
  canEdit: boolean;
  canSeePeople: boolean;
}) {
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);

  const teamsQuery = useTeams({ includeArchived: showArchived });
  const team = teamsQuery.data?.find((t) => t.id === editing);

  return (
    <div className="flex flex-col gap-3">
      {teamsQuery.isError && (
        <Alert tone="erro" title="Não foi possível carregar os times.">
          {teamsQuery.error.message}
        </Alert>
      )}

      {(creating || team) && canEdit && (
        <TeamForm
          key={team?.id ?? 'novo'}
          team={team}
          canSeePeople={canSeePeople}
          toClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}

      {(!teamsQuery.isError || teamsQuery.data) && (
        <Card
          title="Times"
          /*
           * "Sem premiação" está na descrição porque a ausência é a decisão, e
           * porque o sistema antigo tem o campo e a tela dele promete
           * "premiação garantida" — sem uma linha de código que a divida entre
           * as pessoas, a some ao custo de alguém ou a pague.
           */
          descricao="Cada time tem um líder e membros, e os membros respondem ao líder. Quem lidera é membro de um time — o que lidera, ou o de cima —, e é assim que a cadeia de superiores se forma."
          bleed
        >
          <Table
            /* o recorte no painel e a criação na barra — ver o cartão acima */
            toolbar={
              <>
                <Filters
                  values={{ archived: showArchived ? 'sim' : '' }}
                  onApply={(values) => setShowArchived(values.archived === 'sim')}
                  fields={[
                    {
                      type: 'selecao',
                      name: 'archived',
                      label: 'Arquivados',
                      placeholder: 'Só os vigentes',
                      options: [{ value: 'sim', label: 'Vigentes e arquivados' }],
                    },
                  ]}
                />
                {canEdit && <Button onClick={() => setCreating(true)}>Criar time</Button>}
              </>
            }
            label="Times desta base"
            items={teamsQuery.data ?? []}
            loading={teamsQuery.isPending}
            rowKey={(t) => String(t.id)}
            empty="Nenhum time nesta base. Time é agrupamento: sem nenhum, os consultores ficam sem time — o que é um estado legítimo, e não uma pendência."
            rowTitle={(t) => t.nome}
            rowSubtitle={(t) => t.leader?.nome ?? 'Sem líder'}
            rowIcon="pessoas"
            rowActions={(t): Action[] =>
              canEdit
                ? [
                    {
                      label: 'Editar o time',
                      icon: 'etiqueta',
                      disabled: t.archivedAt
                        ? 'Este time está arquivado.'
                        : (false as const),
                      run: () => {
                        setCreating(false);
                        setEditing(t.id);
                      },
                    },
                  ]
                : []
            }
            columns={[
              {
                title: 'Time',
                alignment: 'inicio' as const,
                minWidth: '12rem',
                cell: (t) => (
                  <p className={t.archivedAt ? 'truncate text-texto-suave' : 'truncate'}>
                    {t.nome}
                    {t.archivedAt && (
                      <Badge tone="neutro" className="ml-2">
                        Arquivado
                      </Badge>
                    )}
                  </p>
                ),
              },
              {
                title: 'Líder corrente',
                minWidth: '12rem',
                cell: (t) =>
                  t.leader ? (
                    t.leader.nome
                  ) : (
                    /*
                     * "Sem líder" é estado legítimo — time recém-criado, líder
                     * que saiu e ainda não foi substituído —, e a tela o diz em
                     * vez de fabricar um nome.
                     */
                    <span className="text-texto-suave">Sem líder</span>
                  ),
              },
              {
                title: 'Integrantes',
                alignment: 'fim',
                cell: (t) => (
                  <div className="flex flex-col items-end gap-1">
                    <span className="tabular-nums">{t.members}</span>
                    {/*
                     * O cadastro de antes de 23/09: membros no time que a cadeia
                     * não liga ao líder. A premiação de líder não os soma, e o
                     * selo é o que faz alguém abrir o time e salvar com a data.
                     */}
                    {t.unlinkedMembers > 0 && (
                      <Badge tone="alerta">
                        {t.unlinkedMembers === 1
                          ? '1 sem responder ao líder'
                          : `${t.unlinkedMembers} sem responder ao líder`}
                      </Badge>
                    )}
                  </div>
                ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}

/**
 * Criar ou editar um time.
 *
 * O mesmo formulário para os dois porque são os mesmos dois campos, e uma cópia
 * para "editar" divergiria do "criar" na primeira mudança de rótulo.
 *
 * `leader` é o líder **corrente**, e precisa ser consultor desta base. Salvar
 * grava a cadeia: todo membro passa a responder ao líder a partir da data do
 * formulário. Quem era o líder em março continua respondendo pela hierarquia,
 * que guarda a vigência — nunca por este campo.
 *
 * O líder que não é membro de time nenhum entra neste. O que já é membro de
 * outro time fica lá, e este time passa a estar abaixo daquele.
 */
function TeamForm({
  team,
  canSeePeople,
  toClose,
}: {
  team: SalesTeam | undefined;
  canSeePeople: boolean;
  toClose: () => void;
}) {
  const client = useQueryClient();
  const [nome, setName] = useState(team?.nome ?? '');
  const [leaderUserId, setLeaderUserId] = useState(team?.leader?.id ?? '');
  const [effectiveFrom, setEffectiveFrom] = useState(businessDate(new Date()));
  const [error, setError] = useState<string | null>(null);
  /*
   * O líder sai dos CONSULTORES, e não de todas as pessoas da base: é pelo
   * vínculo de consultor que ele é membro de um time, e um líder que não faz
   * parte de time nenhum foi o defeito apontado em 23/09.
   */
  const consultants = useConsultants({ enabled: canSeePeople });

  const save = useMutation({
    mutationFn: async (archive: boolean) => {
      if (team && archive) {
        const r = await api.sales.editTeam({
          params: { id: team.id },
          body: { action: 'arquivar' },
        });
        return ok(r, 200, 'Não foi possível arquivar o time.');
      }
      if (team) {
        const r = await api.sales.editTeam({
          params: { id: team.id },
          body: {
            action: 'editar',
            nome: nome.trim(),
            leaderUserId: leaderUserId || null,
            effectiveFrom,
          },
        });
        return ok(r, 200, 'Não foi possível salvar o time.');
      }
      const r = await api.sales.createTeam({
        body: { nome: nome.trim(), leaderUserId: leaderUserId || null, effectiveFrom },
      });
      return ok(r, 201, 'Não foi possível criar o time.');
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['comercial'] });
      toClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Modal
      open
      onOpenChange={(next) => {
        if (!next) toClose();
      }}
      size="lg"
      title={team ? `Time ${team.nome}` : 'Criar time'}
      descricao="Os membros deste time respondem ao líder. Salvar grava isso na cadeia de superiores, a partir da data abaixo."
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          save.mutate(false);
        }}
      >
        {error && <Alert tone="erro">{error}</Alert>}
        {!canSeePeople && <WithoutPeople />}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Nome do time"
            required
            maxLength={120}
            value={nome}
            onChange={(e) => setName(e.target.value)}
          />
          <Select
            label="Líder"
            disabled={!canSeePeople}
            value={leaderUserId}
            onChange={(e) => setLeaderUserId(e.target.value)}
            help="Um consultor desta base. Sem time, ele entra neste; já num time, fica lá e este passa a estar abaixo daquele."
          >
            <option value="">Sem líder</option>
            {(consultants.data ?? []).map((c) => (
              <option key={c.id} value={c.user.id}>
                {c.user.nome}
                {c.team ? ` — ${c.team.nome}` : ''}
              </option>
            ))}
          </Select>

          <EffectiveFromField
            value={effectiveFrom}
            onChange={setEffectiveFrom}
            help="Vale para quem passa a responder a este líder agora. Para antecipar a data de quem já responde, use a ficha do consultor."
          />
        </div>

        <ModalActions>
          <Button type="submit" loading={save.isPending}>
            {team ? 'Salvar' : 'Criar'}
          </Button>
        </ModalActions>
      </form>

      {team && (
        <div className="mt-4 border-t border-borda pt-4">
          <h3 className="text-sm font-semibold">Arquivar este time</h3>
          <p className="mt-1 text-sm text-texto-suave">
            Arquivar em vez de apagar: as vendas e as metas antigas continuam apontando
            para ele. Só se arquiva um time vazio — mova os membros antes, porque é a
            troca de time que fecha a vigência de quem respondia ao líder.
          </p>
          <Button
            variant="perigo"
            className="mt-3"
            loading={save.isPending}
            onClick={() => {
              setError(null);
              save.mutate(true);
            }}
          >
            Arquivar o time
          </Button>
        </div>
      )}
    </Modal>
  );
}

/* ------------------------------------------------------------------------- *
 * Hierarquia
 * ------------------------------------------------------------------------- */

/**
 * A cadeia de superiores — SÓ LEITURA desde 23/09/2026.
 *
 * Ela é derivada dos times, e quem escreve nela são as trocas de time e de
 * líder. Uma tela de "abrir vínculo" ao lado seria a segunda fonte da mesma
 * relação, e foi exatamente essa segunda fonte que deixava o time dizer uma
 * coisa e a cadeia outra.
 */
function HierarchySection() {
  const { data: context } = useSessionContext();
  const [at, setAt] = useState('');
  const tenantId = context?.currentTenant.id;

  const hierarchyQuery = useQuery({
    queryKey: ['comercial', 'hierarquia', tenantId, at],
    queryFn: async () => {
      const r = await api.sales.listHierarchy({
        query: at ? { at } : {},
      });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'a hierarquia desta base'));
      }
      return r.body;
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  return (
    <div className="flex flex-col gap-3">
      {hierarchyQuery.isError && (
        <Alert tone="erro" title="Não foi possível carregar a hierarquia.">
          {hierarchyQuery.error.message}
        </Alert>
      )}

      {(!hierarchyQuery.isError || hierarchyQuery.data) && (
        <Card
          title="Cadeia de superiores"
          descricao="Quem responde a quem, e desde quando. Ela vem dos times: para mudar, troque a pessoa de time ou troque o líder do time — a vigência é gravada com a data que o formulário pergunta."
          bleed
        >
          <Table
            /*
             * O filtro por data é o que responde "quem respondia a quem em
             * março", e é a pergunta que a apuração de uma competência antiga
             * faz. Sem ele, a tela mostraria a cadeia de hoje e alguém
             * concluiria que a apuração de março estava errada.
             *
             * Ele era um `Field` de 160px no cabeçalho do cartão, ao lado do
             * botão de abrir, com um `FieldSlot` só para os dois alinharem. No
             * painel ele é um campo como qualquer outro, e o alinhamento deixa
             * de ser problema de quem escreve a tela.
             */
            toolbar={
              <>
                <Filters
                  values={{ at }}
                  onApply={(values) => setAt(String(values.at ?? ''))}
                  fields={[
                    {
                      type: 'data',
                      name: 'at',
                      label: 'Vigente em',
                      help: 'Vazio traz a cadeia de hoje.',
                    },
                  ]}
                />
              </>
            }
            label="Cadeia de superiores do consultor"
            items={hierarchyQuery.data ?? []}
            loading={hierarchyQuery.isPending}
            rowKey={(v) => String(v.id)}
            empty={
              at
                ? `Ninguém respondia a ninguém nesta base em ${formatDate(at)}. A cadeia pode ter começado depois.`
                : 'Ninguém responde a ninguém nesta base ainda. A cadeia nasce dos times: ponha as pessoas num time com líder, com a data desde quando elas estão lá.'
            }
            rowTitle={(v) => `${v.user.nome} responde a ${v.supervisor.nome}`}
            rowSubtitle={(v) =>
              v.validUntil === null
                ? `De ${formatDate(v.validFrom)} — sem fim declarado`
                : `De ${formatDate(v.validFrom)} a ${formatDate(v.validUntil)}, inclusive`
            }
            rowIcon="pessoas"
            columns={[
              {
                title: 'Pessoa',
                alignment: 'inicio' as const,
                minWidth: '12rem',
                cell: (v) => <span className="truncate">{v.user.nome}</span>,
              },
              {
                title: 'Responde a',
                alignment: 'inicio' as const,
                minWidth: '12rem',
                cell: (v) => <span className="truncate">{v.supervisor.nome}</span>,
              },
              {
                title: 'Vigência',
                minWidth: '13rem',
                cell: (v) => <Term link={v} />,
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}

/**
 * A vigência de um vínculo.
 *
 * `validUntil` nulo é o vínculo **corrente**, sem fim declarado — e a tela diz
 * isso em vez de deixar a coluna vazia, porque uma célula vazia numa coluna de
 * datas lê como dado faltando.
 *
 * "Inclusive" está escrito porque a borda é inclusiva no banco: o último dia
 * ainda vale. Em português "até 31/03" é lido como exclusivo tantas vezes quanto
 * inclusivo, e a diferença aqui é um dia de vínculo numa apuração.
 */
function Term({ link }: { link: SupervisorLink }) {
  return (
    <div className="min-w-0 text-xs">
      <p className="tabular-nums">
        De {formatDate(link.validFrom)}
        {link.validUntil === null
          ? ' — sem fim declarado'
          : ` a ${formatDate(link.validUntil)}, inclusive`}
      </p>
      {link.archivedAt && (
        <Badge tone="neutro" className="mt-1">
          Arquivado — nunca deveria ter existido
        </Badge>
      )}
    </div>
  );
}

/**
 * Em qual lote do mês a taxa de tecnologia é descontada (05/10/2026) — ato
 * próprio do vínculo (`definir_taxa_tecnologia`), com botão próprio, pelo
 * motivo da régua: muda quanto alguém recebe num lote.
 *
 * Aparece para todo perfil, e diz quando não pesa: só o externo paga a taxa.
 * Esconder o seletor do interno faria a escolha sumir no dia em que ele
 * virasse externo, sem ninguém ter visto qual era.
 */
function TechnologyFeeLotBlock({ consultant }: { consultant: Consultant }) {
  const client = useQueryClient();
  const [choice, setChoice] = useState<TechnologyFeeLot>(consultant.technologyFeeLot);
  const [saved, setSaved] = useState<TechnologyFeeLot>(consultant.technologyFeeLot);
  const [error, setError] = useState<string | null>(null);
  const pays = consultant.profile === 'externo';

  const save = useMutation({
    mutationFn: async (technologyFeeLot: TechnologyFeeLot) =>
      ok(
        await api.sales.editConsultant({
          params: { id: consultant.id },
          body: { action: 'definir_taxa_tecnologia', technologyFeeLot },
        }),
        200,
        'Não foi possível salvar o lote da taxa de tecnologia.',
      ),
    onSuccess: (updated) => {
      setError(null);
      setSaved(updated.technologyFeeLot);
      void client.invalidateQueries({ queryKey: ['comercial'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <EditorTile>
      {error && <Alert tone="erro">{error}</Alert>}
      <Select
        label="Lote da taxa de tecnologia"
        value={choice}
        onChange={(e) => setChoice(e.target.value as TechnologyFeeLot)}
        help={
          pays
            ? 'Vale a partir do mês sem nota enviada.'
            : 'Só o consultor externo paga a taxa.'
        }
      >
        {TECHNOLOGY_FEE_LOTS.map((value) => (
          <option key={value} value={value}>
            {TECHNOLOGY_FEE_LOT_LABELS[value]}
          </option>
        ))}
      </Select>
      <div className="mt-auto flex justify-end">
        <Button
          variant="contorno"
          loading={save.isPending}
          disabled={choice === saved}
          onClick={() => save.mutate(choice)}
        >
          Salvar lote
        </Button>
      </div>
    </EditorTile>
  );
}
